import theme from "tools/theme";

export const DEFAULT_COLOR = theme.palette.primary.main;
export const DEFAULT_OPACITY = 100;
const WHITE_RGB = {r: 255, g: 255, b: 255};
const WHITE_WEIGHT = 0.9;

export const BLACK_RGB = {r: 0, g: 0, b: 0};
export const BLACK_WEIGHT = 0.6;

export const rgbaToHex = (r: number, g: number, b: number): string => {
  const toHex = (component: number) => {
    const hex = component.toString(16).toUpperCase();

    return hex.length === 1 ? `0${hex}` : hex;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

// Six uppercase HEX digits without `#` for a Quill format value (`#rgb`,
// `#rrggbb` or `rgb(...)`), or null when there is no single colour.
export const toHex6 = (value?: string | string[]): string | null => {
  if (typeof value !== "string") {
    return null;
  }
  const shortHex = /^#([\da-f])([\da-f])([\da-f])$/i.exec(value);
  if (shortHex) {
    return shortHex
      .slice(1)
      .map((digit) => digit + digit)
      .join("")
      .toUpperCase();
  }
  if (/^#[\da-f]{6}$/i.test(value)) {
    return value.slice(1).toUpperCase();
  }
  const rgb = /^rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)$/i.exec(value);
  if (rgb) {
    return rgbaToHex(+rgb[1], +rgb[2], +rgb[3]).slice(1);
  }
  return null;
};

export const hexToRgba = (
  hex: string,
  opacity: number
): {r: number; g: number; b: number; a: number} => {
  const normalizedHex = hex.replace("#", "");
  const bigint = parseInt(normalizedHex, 16);
  const r = Math.floor(bigint / (256 * 256));
  const g = Math.floor((bigint / 256) % 256);
  const b = bigint % 256;

  return {r, g, b, a: opacity / DEFAULT_OPACITY};
};

export const getMixedHex = (hex: string, numericOpacity: number): string => {
  const opacityRatio = numericOpacity / 100;
  const foregroundColor = hexToRgba(hex, 100);
  const mixedColor = {
    r: Math.round(
      foregroundColor.r * opacityRatio + WHITE_RGB.r * (1 - opacityRatio)
    ),
    g: Math.round(
      foregroundColor.g * opacityRatio + WHITE_RGB.g * (1 - opacityRatio)
    ),
    b: Math.round(
      foregroundColor.b * opacityRatio + WHITE_RGB.b * (1 - opacityRatio)
    ),
  };

  return rgbaToHex(mixedColor.r, mixedColor.g, mixedColor.b);
};

export const mixHexWithColor = (
  hex: string,
  color = WHITE_RGB,
  weight = WHITE_WEIGHT
): string => {
  const {r, g, b} = hexToRgba(hex, DEFAULT_OPACITY);

  const rgbWithColor = {
    r: Math.round(r * (1 - weight) + color.r * weight),
    g: Math.round(g * (1 - weight) + color.g * weight),
    b: Math.round(b * (1 - weight) + color.b * weight),
  };

  return rgbaToHex(rgbWithColor.r, rgbWithColor.g, rgbWithColor.b);
};
